import { isEmpty } from 'underscore';
import { useCallback, useContext, useMemo, useRef, useEffect } from 'react';
import { useInterpret, useSelector } from '@xstate/react';
import { withProps } from '@bingads-webui-react/with-props';
import { useRefInit } from '@bingads-webui-campaign-react/common-generic-hooks';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import {
  wizardStateMachine, WizardEvents,
  isBusySelector, stepIndexSelector, stepServicesCacheSelector,
  wasSavingSelector, wasWaitingForConfirmationSelector, wasStoppingSelector, stateLoggingListener,
} from '@bingads-webui-campaign-react/step-wizard-machine';
import { StepRepository } from '../shared/step-repository';

const getStepRepository = stepInfo => (!stepInfo.stepRepositoryConfig && !stepInfo.StepRepository ?
  undefined :
  withProps(
    stepInfo.stepRepositoryConfig || {},
    stepInfo.StepRepository || StepRepository
  ));

export const useWizardState = (props) => {
  const {
    routeToNavigateToWhenDone,
    scopeToRemoveWhenDone,
    scope,
    instrumentation,
    permissions,
    extraStepsData = {},
    wizardScenario: {
      scenarioName, isCenteredLayout, isEditing, stepList, withContentFooter,
    },
    machineId = 'StepWizard',
  } = props;
  const { scenario, activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName, // TODO: consider '...ForRetail' and '...ForSearch
    activityName: isEditing ? 'Settings' : 'Creation',
  }), [instrumentation, isEditing, scenarioName]);

  useEffect(() => {
    setClarityTag({ StepWizardScenario: scenarioName });
  }, [scenarioName]);

  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const onDone = useCallback(() => {
    if (!isEmpty(scopeToRemoveWhenDone)) {
      scopeRef.current.remove(scopeToRemoveWhenDone);
    }
    scopeRef.current.save(routeToNavigateToWhenDone);
  }, [routeToNavigateToWhenDone, scopeToRemoveWhenDone]);

  // The xstate-based wizard state machine must be created only once per the lifetime of the component.
  // If props' changes need to be reflected in the state machine, the state machine must provide
  // event-based API to communicate such changes.
  const wizardMachineRef = useRefInit(() => wizardStateMachine({
    machineId,
    wizardOptions: {
      stepList,
      onDone,
      stepsData: {
        permissions,
        isEditing,
        scenarioName,
        scenario,
        activity,
        i18n,
        serverContext,
        scope,
        ...scope.values,
        ...extraStepsData,
      },
    },
  }));
  const wizardMachine = wizardMachineRef.current;
  const service = useInterpret(wizardMachine, undefined, stateLoggingListener);
  const { send } = service;

  const stepServicesCache = useSelector(service, stepServicesCacheSelector);
  const stepListWithServiceInfo = useMemo(() => stepList.map(stepInfo2 => ({
    stepInfo: stepInfo2,
    stepService: stepServicesCache[stepInfo2.id],
    StepRepository: getStepRepository(stepInfo2),
  })), [stepList, stepServicesCache]);

  const stepIndex = useSelector(service, stepIndexSelector);
  const isBusy = useSelector(service, isBusySelector);
  const wasSaving = useSelector(service, wasSavingSelector);
  const wasWaitingForConfirmation = useSelector(service, wasWaitingForConfirmationSelector);
  const wasStopping = useSelector(service, wasStoppingSelector);

  const stepInfo = useMemo(() => stepList[stepIndex], [stepIndex, stepList]);

  const isReadOnlyUser = useMemo(() => permissions.IsReadOnlyUser || serverContext.CurrentAccount.Status === 32, [permissions.IsReadOnlyUser, serverContext.CurrentAccount.Status]); // 32 is InActive

  const state = {
    isReadOnlyUser,
    isCenteredLayout,
    withContentFooter,
    i18n,
    scenarioName,
    scenario,
    activity,

    stepListWithServiceInfo,
    stepIndex,
    stepInfo,
    isBusy,
    wasSaving,
    wasWaitingForConfirmation,
    wasStopping,
  };


  const saveAndNavigateNext = useCallback(() => {
    send(WizardEvents.NAVIGATE_NEXT);
  }, [send]);
  const saveAndNavigateBack = useCallback(() => {
    send(WizardEvents.NAVIGATE_BACK);
  }, [send]);
  const cancelAndExit = useCallback(() => {
    send(WizardEvents.CANCEL);
  }, [send]);
  const saveAndExit = useCallback(() => {
    send(WizardEvents.EXIT);
  }, [send]);

  useEffect(() => {
    window.addEventListener('popstate', cancelAndExit);

    return () => {
      window.removeEventListener('popstate', cancelAndExit);
    };
  }, [cancelAndExit]);

  const api = {
    saveAndNavigateNext, saveAndNavigateBack, cancelAndExit, saveAndExit,
  };

  return [state, api];
};
